import { useCallback, useEffect, useState } from 'react';
import { DetailedIconChecklist } from '@zillow/constellation';
import { Alert, Box, Button, InlineFeedback, Toast, useToast } from '@zillow/constellation-10';

import { PartialMessage } from '@zg-rentals/backend-styled-components';

import { useSelector } from '../../../../selectors/useSelector';

import ConnectedModuleCard from '../../../ConnectedModuleCard';
import {
  useAddToPropertyConditionWaitlistMutation,
  useIsOnPropertyConditionWaitlistQuery,
} from '../../../../hooks/queries/propertycondition';
import { EmptyStateTitleText } from '../../../../styles';

import type { PartialMessageProps } from '@zg-rentals/backend-styled-components';

type Props = {
  isUnsupportedStateError: boolean;
  partialErrorMessages: Array<PartialMessageProps>;
};

const PropertyConditionErrorModule = ({ isUnsupportedStateError, partialErrorMessages }: Props) => {
  const {
    properties: {
      currentProperty: { listingAlias },
    },
  } = useSelector((state) => state);

  const [isOnWaitlist, setIsOnWaitlist] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const addToWaitlist = useAddToPropertyConditionWaitlistMutation(listingAlias);
  const { data, isLoading } = useIsOnPropertyConditionWaitlistQuery(listingAlias);

  useEffect(() => {
    data !== undefined && setIsOnWaitlist(data);
  }, [data]);

  const { enqueueToast } = useToast();
  const onJoiningWaitlistSuccessToast = useCallback(() => {
    enqueueToast(
      <Toast tone="success">
        We have added you to our waitlist. We will email you once property condition report is available in your area.
      </Toast>,
    );
  }, []);

  async function onJoinWaitlistClick(): Promise<void> {
    setIsSaving(true);
    const response = await addToWaitlist.mutateAsync();
    setIsSaving(false);
    if (response.isOnWaitlist) {
      onJoiningWaitlistSuccessToast();
    }
  }

  return (
    <ConnectedModuleCard isLoading={isLoading}>
      <Box
        css={{ display: 'flex', gap: '200', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}
      >
        <DetailedIconChecklist />
        <EmptyStateTitleText>Property condition report</EmptyStateTitleText>
        <Alert tone="warning">
          <PartialMessage partialMessages={partialErrorMessages} />
        </Alert>
        <Button
          size="md"
          css={{ marginTop: '200' }}
          loading={isLoading || isSaving}
          disabled={isOnWaitlist || !isUnsupportedStateError}
          onClick={onJoinWaitlistClick}
          tone="brand"
          emphasis="filled"
        >
          Join waitlist
        </Button>
        {isOnWaitlist && <InlineFeedback tone="info">You&apos;re already on the waitlist.</InlineFeedback>}
      </Box>
    </ConnectedModuleCard>
  );
};

export default PropertyConditionErrorModule;
